import { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../lib/api";
import type {
  CommissionCalculation,
  CommissionStructure,
  CommissionStructureSettings,
} from "../../types/commissionStructure";
import { CommissionStructureEditor } from "./CommissionStructureEditor";

export function CommissionSettings({
  jobId,
  onSaved,
}: {
  jobId?: string;
  onSaved?: () => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const base = jobId ? `/api/jobs/${jobId}` : "/api/organizations";
  const scope = `${user?.id}:${base}`;
  const key = ["commission-structure", scope];
  const settings = useQuery({
    queryKey: key,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (
        await apiClient.get<CommissionStructureSettings>(
          `${base}/commission-structure/`,
        )
      ).data,
  });
  const save = useMutation({
    mutationFn: async (structure: CommissionStructure | null) =>
      (
        await apiClient.patch<CommissionStructureSettings>(
          `${base}/commission-structure/`,
          { structure, expected_revision: settings.data!.revision },
        )
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: ["jobFinancialSummary"] });
      queryClient.invalidateQueries({ queryKey: ["commissions"] });
      setSaved(true);
      onSaved?.();
    },
  });
  if (settings.isPending)
    return <CircularProgress aria-label="Loading commission settings" />;
  if (settings.isError)
    return (
      <Alert color="danger">
        Unable to load commission settings.{" "}
        <Button onClick={() => settings.refetch()}>Retry</Button>
      </Alert>
    );
  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} alignItems="center">
        <Chip
          size="sm"
          color={settings.data.source === "job" ? "warning" : "neutral"}
        >
          {settings.data.source === "job"
            ? "Job override"
            : settings.data.source === "legacy"
              ? "Existing formula"
              : "Organization default"}
        </Chip>
        <Typography level="body-sm">
          {jobId
            ? "Preview uses this job’s actual costs, credits, and sales rep."
            : "Individual user commission rates remain available in every structure."}
        </Typography>
      </Stack>
      {saved && <Alert color="success">Commission structure saved.</Alert>}
      <CommissionStructureEditor
        previewScope={scope}
        key={settings.data.revision}
        initial={settings.data.effective_structure}
        job={!!jobId}
        saving={save.isPending}
        saveError={save.error?.message}
        onSave={(value) => {
          setSaved(false);
          save.mutate(value);
        }}
        onReset={
          jobId && settings.data.structure ? () => save.mutate(null) : undefined
        }
        preview={async (structure, sample) => {
          const response = jobId
            ? await apiClient.post<CommissionCalculation>(
                `${base}/commission-preview/`,
                { structure },
              )
            : await apiClient.patch<CommissionCalculation>(
                `${base}/commission-preview/`,
                { structure, ...sample },
              );
          return response.data;
        }}
      />
      {save.isError && (
        <Button variant="plain" onClick={() => settings.refetch()}>
          Reload saved settings
        </Button>
      )}
    </Stack>
  );
}
